export interface ISalesRepAnalysisResponse
{
	Id: string;
	SalesRepUserId: string;
	SalesRepName: string;
	Report?: IAnalysisReport;
	SnapshotDate?: string;
	SellerAnalyses: ISellerAnalysisResponse[];
	AvailableDates: string[];
}

/**
 * Structured analysis report returned by the AI
 */
export interface IAnalysisReport {
	Id: string;
	TargetEntityId: string;
	Persona: AnalysisPersona;
	Subject: AnalysisSubject;
	SnapshotDate: string;
	/** High-level summary of field conditions */
	Overview: string;
	/** Technical summary of findings -- not intended to be human readable */
	Summary:  string;
	/** List of specific concerns or issues identified */
	Concerns: IConcern[];
}

/**
 * Individual concern or issue identified in the field analysis
 */
export interface IConcern
{
	Id: string;
	/** Broad category of the concern (e.g., "Disease", "Nutrient") */
	Category: string;
	/** Brief title describing the concern */
	Headline: string;
	/** Detailed explanation of the concern */
	Details: string;
	/** Severity level: critical, very high, high, medium, low, very low */
	Severity: string;
	/** Actionable advice to address the concern */
	Recommendation: string;
}

export const severityOrder: Record<string, number> = {
	critical: 6,
	'very high': 5,
	high: 4,
	medium: 3,
	low: 2,
	'very low': 1,
	none: 0,
};

/**
 * Maps severity levels to their corresponding color codes for visual indication
 * @param severity - The severity level (case-insensitive): critical, very high, high, medium, low, very low
 * @returns Hex color code string
 */
export const getSeverityColor = (severity: string): string => 
{
	const severityLower = severity.toLowerCase().trim();
	switch (severityLower) 
	{
		case 'critical':
			return '#eb4b4b';
		case 'very high':
			return '#ED965D';
		case 'high':
			return '#F0C355';
		case 'medium':
			return '#31B4F2';
		case 'low':
			return '#19A04B';
		case 'very low':
			return '#005693';
		default:
			return '#999999';
	}
};

export const normalizeSeverity = (severity?: string): string => (severity ?? 'none').toLowerCase().trim();

const getSeverityRank = (severity?: string): number =>
{
	if (!severity)
	{
		return -1;
	}
	const key = severity.trim().toLowerCase();
	return severityOrder[key] ?? -1;
};

export const getHighestSeverity = (concerns: IConcern[]) =>
{
	if (!concerns || concerns.length === 0) return {severity: 'none', count: 0};

	return concerns.reduce((current, next) =>
	{
		const nextKey = normalizeSeverity(next.Severity);
		const currentKey = normalizeSeverity(current.severity);

		// Found a higher severity, reset our counter.
		if(severityOrder[nextKey] > severityOrder[currentKey])
		{
			return { severity: nextKey, count: 1 };
		}

		// Found another of the same severity, increment our counter.
		if(severityOrder[nextKey] === severityOrder[currentKey])
		{
			return { severity: currentKey, count: current.count + 1 };
		}

		// Found a lower severity, return unchanged
		return current;
	}, { severity: 'none', count: 0 });
};

/**
 * Sorts a list of items by highest severity.
 *
 * @param items - Array of items
 * @param getConcerns - Function that extracts IConcern[] from each item
 * @param getName - Optional function that extracts name from each item for secondary sort
 */
export const sortByHighestSeverity = <T>(items: T[], getConcerns: (item: T) => IConcern[], getName?: (item: T) => string): Array<{ item: T; highestSeverity: string, count: number }> =>
{
	return items
		.map(item =>
		{
			const concerns = getConcerns(item) || [];
			const highestSeverity = getHighestSeverity(concerns);
			return { item, highestSeverity: highestSeverity.severity, count: highestSeverity.count };
		})
		.sort((a, b) => 
		{
			const severityDiff = (severityOrder[b.highestSeverity] ?? 0) - (severityOrder[a.highestSeverity] ?? 0);
			if (severityDiff !== 0) return severityDiff;
			
			// Secondary sort by name (ascending alphabetical)
			if (getName) 
			{
				const nameA = getName(a.item).toLowerCase();
				const nameB = getName(b.item).toLowerCase();
				return nameA.localeCompare(nameB);
			}
			return 0;
		});
};

type SeveritySortDirection = 'desc' | 'asc';
/**
 * Sorts a list of concerns by severity.
 * @param concerns 
 * @param direction 
 * @returns 
 */
export const sortConcernsBySeverity = <T extends { Severity?: string; Headline?: string }>(concerns: T[], direction: SeveritySortDirection = 'desc'): T[] =>
{
	const multiplier = direction === 'desc' ? -1 : 1;

	return [...concerns].sort((a, b) =>
	{
		const ra = getSeverityRank(a.Severity);
		const rb = getSeverityRank(b.Severity);

		if (ra !== rb)
		{
			return (ra - rb) * multiplier;
		}

		// tie-breaker = alphabetical by headline
		const ha = (a.Headline ?? '').toLowerCase();
		const hb = (b.Headline ?? '').toLowerCase();
		return ha.localeCompare(hb);
	});
};

export interface ISellerAnalysisResponse
{
	SellerUserId: string;
	SellerName: string;
	FoundationId: string;
	Report?: IAnalysisReport;
	SnapshotDate?: string;

	GrowerAnalyses: IGrowerAnalysisResponse[];
	AvailableDates: string[];
}

export interface IGrowerAnalysisResponse
{
	GrowerId: string;
	GrowerName: string;
	FoundationId: string;
	Report?: IAnalysisReport;
	SnapshotDate?: string;
	AvailableDates: string[];
}

/**
 * Response structure from the AI analysis API endpoint
 */
export interface IAiAnalysisResponse
{
	/** Name of the AI provider used (e.g., "CropwiseAi") */
	Provider:     string;
	/** Specific model identifier used for analysis */
	Model:       string;
	/** The complete analysis results */
	Analysis:     IAnalysisReport;
	/** System prompt used to format the AI response */
	FormatPrompt: string;
	/** User/domain-specific prompt sent to the AI */
	TaskPrompt:   string;

	/** Number of tokens in the input prompt */
	InputTokenCount?: number;
	/** Number of tokens in the AI response */
	OutputTokenCount?: number;
	/** Estimated cost of the API call in USD */
	CostEstimate?: number;
	/** Time taken to complete the analysis in milliseconds */
	ElapsedMs: number;
}

export enum AnalysisPersona
{
    Grower,
    Seller,
    SalesRep,
    DistrictManager,
    Agronomist
}

export enum AnalysisSubject
{
    Field,
    Grower,
    Seller,
    SalesRep,
    Territory
}

export enum FeedbackRating
{
	Unknown,
	Positive,
	Negative,
}
